import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import DateRangePicker from "@/pages/Dashboard/Partials/DateRangePicker";
import { ListFilter } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import type { FilterBadge } from "../hooks/useAnalyticsPanel";

const MAX_VISIBLE_FILTERS = 15;

interface AnalyticsToolbarProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange | undefined) => void;
  onOpenFilters: () => void;
  hasActiveFilters: boolean;
  filterBadges: FilterBadge[];
  onClearFilters: () => void;
}

export function AnalyticsToolbar({
  dateRange,
  onDateRangeChange,
  onOpenFilters,
  hasActiveFilters,
  filterBadges,
  onClearFilters,
}: AnalyticsToolbarProps) {
  const { t } = useTranslation(["reports", "common"]);
  const hiddenBadges = filterBadges.slice(MAX_VISIBLE_FILTERS);

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <DateRangePicker
            widthFull
            defaultValue={dateRange}
            onChange={onDateRangeChange}
          />
          <Button variant="secondary" onClick={onOpenFilters}>
            <ListFilter size={20} />
            {t("reports:filters")}
          </Button>
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2">
            {filterBadges.slice(0, MAX_VISIBLE_FILTERS).map((badge) => (
              <Badge
                key={badge.key}
                className="flex font-medium min-h-7 items-center hover:opacity-80 hover:bg-[#18181b] gap-4 bg-[#18181b] text-colorText"
              >
                <div className="flex text-colorText items-center gap-1">
                  <span className="text-muted">{badge.group}</span>
                  {badge.label}
                </div>
                <button
                  type="button"
                  onClick={badge.onClick}
                  className="text-muted cursor-pointer"
                >
                  ✕
                </button>
              </Badge>
            ))}

            {hiddenBadges.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-pointer rounded bg-[#27272A] px-1.5 py-0.5 text-xs font-semibold flex items-center justify-center">
                    +{hiddenBadges.length}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="w-80 p-4 bg-[#18181b] text-colorText border border-[#333]">
                  <h4 className="font-semibold mb-2">{t("reports:filters")}</h4>
                  <div className="flex flex-col gap-2 max-h-60 overflow-y-auto px-4">
                    {hiddenBadges.map((badge) => (
                      <div
                        key={badge.key}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="gap-1 flex items-start">
                          <span className="text-muted">{badge.group}</span>
                          <span>{badge.label}</span>
                        </div>
                        <button
                          type="button"
                          onClick={badge.onClick}
                          className="text-muted hover:text-white ml-2"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            )}

            <Button
              variant="secondary"
              onClick={onClearFilters}
              className="ml-2 h-7 flex items-center gap-4 bg-transparent text-interactive"
            >
              {t("common:clear_all_filters")}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
